import React, { useState } from 'react';

const ContactMain = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
    });
    
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

    const validateForm = () => {
        const newErrors = {};
        
        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters';
        }
        
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        
        // Phone validation
        const phoneRegex = /^[\d\s()+-]+$/;
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!phoneRegex.test(formData.phone) || formData.phone.replace(/\D/g, '').length < 10) {
            newErrors.phone = 'Please enter a valid phone number';
        }
        
        // Service validation
        if (!formData.service || formData.service === 'Select a Service') {
            newErrors.service = 'Please select a service';
        }
        
        // Message validation
        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        } else if (formData.message.trim().length < 10) {
            newErrors.message = 'Message must be at least 10 characters';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setIsSubmitting(true);
        setSubmitStatus({ type: '', message: '' });
        
        try {
            // Send email via API endpoint
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    service: formData.service,
                    message: formData.message,
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to send message');
            }
            
            setSubmitStatus({
                type: 'success',
                message: 'Thank you! Your message has been sent. We\'ll get back to you within one business day.'
            });
            
            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                service: '',
                message: ''
            });
        } catch (error) {
            // Log error to monitoring service in production
            // console.error('Form submission error:', error);
            setSubmitStatus({
                type: 'error',
                message: 'Sorry, there was an error sending your message. Please try again or call us at 800-721-5822.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <React.Fragment>
            {/*Contact One Start*/}
          <section className="contact-one">
            <div className="container">
              <div className="contact-one__inner">
                <h3 className="contact-one__title">Let's Start Your Project</h3>
                <p className="contact-one__text">
                  Have a question or ready to place an order? Fill out the form below and we'll get back to you within one business day.
                </p>
                <form
                  className="contact-form-validated contact-one__form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="row">
                    <div className="col-xl-6 col-lg-6">
                      <div className="contact-one__input-box">
                        <input
                          type="text"
                          name="name"
                          placeholder="Your Name"
                          value={formData.name}
                          onChange={handleChange}
                          className={errors.name ? 'error' : ''}
                          required
                        />
                        {errors.name && <span className="error-message">{errors.name}</span>}
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-6">
                      <div className="contact-one__input-box">
                        <input
                          type="email"
                          name="email"
                          placeholder="Your Email"
                          value={formData.email}
                          onChange={handleChange}
                          className={errors.email ? 'error' : ''}
                          required
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-6">
                      <div className="contact-one__input-box">
                        <input
                          type="text"
                          name="phone"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleChange}
                          className={errors.phone ? 'error' : ''}
                          required
                        />
                        {errors.phone && <span className="error-message">{errors.phone}</span>}
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-6">
                      <div className="contact-one__input-box">
                        <div className="select-box">
                          <select 
                            className={`selectmenu wide ${errors.service ? 'error' : ''}`}
                            name="service"
                            value={formData.service}
                            onChange={handleChange}
                          >
                            <option value="">Select a Service</option>
                            <option value="Custom Apparel">Custom Apparel</option>
                            <option value="Promotional Products">Promotional Products</option>
                            <option value="Corporate Gifts">Corporate Gifts</option>
                            <option value="Event Materials">Event Materials</option>
                            <option value="Rush Orders">Rush Orders</option>
                            <option value="Other/Custom Request">Other/Custom Request</option>
                          </select>
                        </div>
                        {errors.service && <span className="error-message">{errors.service}</span>}
                      </div>
                    </div>
                    <div className="col-xl-12">
                      <div className="contact-one__input-box text-message-box">
                        <textarea
                          name="message"
                          placeholder="Tell us about your project (quantity, timeline, budget, etc.)"
                          value={formData.message}
                          onChange={handleChange}
                          className={errors.message ? 'error' : ''}
                        ></textarea>
                        {errors.message && <span className="error-message">{errors.message}</span>}
                      </div>
                      <div className="contact-one__btn-box">
                        <button
                          type="submit"
                          className="thm-btn contact-one__btn"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Sending...' : 'Send Message'}
                          <span className="icon-arrow-right"></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
                {submitStatus.message && (
                  <div className={`result ${submitStatus.type === 'success' ? 'success' : 'error'}`}>
                    {submitStatus.message}
                  </div>
                )}
              </div>
            </div>
          </section>
          {/*Contact One End*/}

          {/*Contact Two Start*/}
          <section className="contact-two">
            <div className="container">
              <div className="row">
                {/* Contact Two Single Start */}
                <div className="col-xl-4 col-lg-4">
                  <div className="contact-two__single">
                    <div className="contact-two__icon">
                      <span className="fas fa-phone"></span>
                    </div>
                    <h3 className="contact-two__title">Contact Information</h3>
                    <p className="contact-two__text">
                      <a href="tel:8007215822" style={{color: 'inherit'}}>800-721-5822</a><br />
                      <a href="mailto:general@ubranditpa.com" style={{color: 'inherit'}}>general@ubranditpa.com</a><br />
                      Mon-Fri: 9AM-5PM EST
                    </p>
                  </div>
                </div>
                {/* Contact Two Single End */}

                {/* Contact Two Single Start */}
                <div className="col-xl-4 col-lg-4">
                  <div className="contact-two__single">
                    <div className="contact-two__icon">
                      <span className="fas fa-headset"></span>
                    </div>
                    <h3 className="contact-two__title">Customer Service</h3>
                    <p className="contact-two__text">
                      Order Status Inquiries<br />
                      Design Assistance<br />
                      Rush Order Requests
                    </p>
                  </div>
                </div>
                {/* Contact Two Single End */}

                {/* Contact Two Single Start */}
                <div className="col-xl-4 col-lg-4">
                  <div className="contact-two__single">
                    <div className="contact-two__icon">
                      <span className="fas fa-building"></span>
                    </div>
                    <h3 className="contact-two__title">About U Brand It</h3>
                    <p className="contact-two__text">
                      Serving Nationwide<br />
                      Over 10,000 Products<br />
                      Family-Owned
                    </p>
                  </div>
                </div>
                {/* Contact Two Single End */}
              </div>
            </div>
          </section>
          {/*Contact Two End*/}

          {/*Bottom CTA Start*/}
          <section className="cta-one" style={{ paddingTop: '80px', paddingBottom: '80px' }}>
            <div className="container">
              <div className="text-center">
                <h3 className="cta-one__title" style={{ marginBottom: '20px' }}>
                  Ready to Build Your Brand?
                </h3>
                <p className="cta-one__text" style={{ fontSize: '18px', marginBottom: '30px' }}>
                  Join thousands of satisfied customers who trust U Brand It for their promotional needs.
                </p>
                <button type="button" className="thm-btn" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
                  Start Your Order <span className="icon-arrow-right"></span>
                </button>
              </div>
            </div>
          </section>
          {/*Bottom CTA End*/}
        </React.Fragment>
    );
};

export default ContactMain;
